import { Group, Paper, Table, Text, Title } from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  DeploymentGroup,
  DeploymentGroupGpuLoad,
  DeploymentGroupMetrics,
  DeploymentGroupRates
} from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getRelativeTimeFromNow } from '@/utils/date'
import { getLoadGpuName, getWorstCameraName } from './groupLoadNames'
import { formatRate } from './memberRate'

type LoadRowProps = {
  labelId: string
  labelValues?: Record<string, number>
  children: ReactNode
}

const LoadRow = ({ labelId, labelValues, children }: LoadRowProps) => (
  <Table.Tr>
    <Table.Th w={160}>
      <FormattedMessage id={labelId} values={labelValues} />
    </Table.Th>
    <Table.Td>{children}</Table.Td>
  </Table.Tr>
)

type PairProps = {
  value: number | null
  max: number | null
  pairId: string
  singleId: string
}

const Pair = ({ value, max, pairId, singleId }: PairProps) => {
  if (value === null) {
    return '-'
  }

  if (max === null) {
    return (
      <FormattedMessage id={singleId} values={{ value: formatRate(value) }} />
    )
  }

  return (
    <FormattedMessage
      id={pairId}
      values={{ value: formatRate(value), max: formatRate(max) }}
    />
  )
}

const Single = ({ value, id }: { value: number | null; id: string }) =>
  value === null ? (
    '-'
  ) : (
    <FormattedMessage id={id} values={{ value: formatRate(value) }} />
  )

type GpuRowsProps = {
  load: DeploymentGroupGpuLoad
  gpuName?: string | null
}

const GpuRows = ({ load, gpuName }: GpuRowsProps) => (
  <>
    <LoadRow labelId="deployments.groups.details.load.gpu">
      {load.index === null ? (
        '-'
      ) : gpuName ? (
        <FormattedMessage
          id="deployments.list.group.gpu"
          values={{ index: load.index, name: gpuName }}
        />
      ) : (
        <FormattedMessage
          id="deployments.groups.details.allowedGpus.unnamed"
          values={{ index: load.index }}
        />
      )}
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.utilisation">
      <Single
        value={load.utilization_percent}
        id="deployments.groups.details.load.utilisation.value"
      />
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.power">
      <Pair
        value={load.power_w}
        max={load.power_limit_w}
        pairId="deployments.groups.details.load.power.value"
        singleId="deployments.groups.details.load.power.value.noLimit"
      />
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.clock">
      <Pair
        value={load.sm_clock_mhz}
        max={load.sm_clock_max_mhz}
        pairId="deployments.groups.details.load.clock.value"
        singleId="deployments.groups.details.load.clock.value.noMax"
      />
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.temperature">
      <Single
        value={load.temperature_c}
        id="deployments.groups.details.load.temperature.value"
      />
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.gpuMemory">
      <Pair
        value={load.memory_used_mb}
        max={load.memory_total_mb}
        pairId="deployments.groups.details.load.gpuMemory.value"
        singleId="deployments.groups.details.load.gpuMemory.value.noTotal"
      />
    </LoadRow>

    <LoadRow labelId="deployments.groups.details.load.groupMemory">
      <Single
        value={load.group_memory_mb}
        id="deployments.groups.details.load.groupMemory.value"
      />
    </LoadRow>
  </>
)

type RateRowsProps = {
  metrics: DeploymentGroupMetrics
  memberCount: number
  worstCameraName: string | null
}

const CounterRows = ({ rates }: { rates: DeploymentGroupRates | null }) => {
  if (!rates) {
    return (
      <Table.Tr>
        <Table.Td colSpan={2}>
          <Text size="sm" c="dimmed">
            <FormattedMessage id="deployments.groups.details.load.noDataYet" />
          </Text>
        </Table.Td>
      </Table.Tr>
    )
  }

  const minutes = Math.round(rates.window_s / 60)
  const events = rates.events_per_min
  const clips = rates.clips_per_min

  return (
    <>
      <LoadRow
        labelId="deployments.groups.details.load.events"
        labelValues={{ minutes }}
      >
        <FormattedMessage
          id="deployments.groups.details.load.events.value"
          values={{
            started: formatRate(events.started),
            delivered: formatRate(events.delivered),
            rejected: formatRate(events.rejected),
            failed: formatRate(events.failed)
          }}
        />
      </LoadRow>

      <LoadRow
        labelId="deployments.groups.details.load.clips"
        labelValues={{ minutes }}
      >
        <FormattedMessage
          id="deployments.groups.details.load.clips.value"
          values={{
            done: formatRate(clips.done),
            partial: formatRate(clips.partial_preroll),
            failed: formatRate(clips.failed)
          }}
        />
      </LoadRow>
    </>
  )
}

const RateRows = ({ metrics, memberCount, worstCameraName }: RateRowsProps) => {
  const { rates, cameras_below_rate, worst_camera } = metrics

  return (
    <>
      <CounterRows rates={rates} />

      {cameras_below_rate !== null && (
        <LoadRow labelId="deployments.groups.details.load.camerasBelowRate">
          {worst_camera && worst_camera.fps !== null ? (
            <FormattedMessage
              id="deployments.groups.details.load.camerasBelowRate.worst"
              values={{
                n: cameras_below_rate,
                m: memberCount,
                name: worstCameraName,
                fps: formatRate(worst_camera.fps),
                source:
                  worst_camera.source_fps === null
                    ? '-'
                    : formatRate(worst_camera.source_fps)
              }}
            />
          ) : (
            <FormattedMessage
              id="deployments.groups.details.load.camerasBelowRate.value"
              values={{ n: cameras_below_rate, m: memberCount }}
            />
          )}
        </LoadRow>
      )}
    </>
  )
}

type DeploymentGroupLoadCardProps = {
  group: DeploymentGroup
  device?: Device
}

export const DeploymentGroupLoadCard = ({
  group,
  device
}: DeploymentGroupLoadCardProps) => {
  const metrics = group.metrics

  if (!metrics) {
    return null
  }

  const gpuName = getLoadGpuName(group, device)
  const worstCameraName = getWorstCameraName(group, device)

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="center" mb="xs">
        <Title order={6}>
          <FormattedMessage id="deployments.groups.details.load" />
        </Title>
        <Text size="xs" c="dimmed">
          <FormattedMessage
            id="deployments.groups.details.load.reported"
            values={{ time: getRelativeTimeFromNow(metrics.reported_at) }}
          />
        </Text>
      </Group>

      <Table variant="vertical" layout="fixed">
        <Table.Tbody>
          {metrics.gpu_load ? (
            <GpuRows load={metrics.gpu_load} gpuName={gpuName} />
          ) : (
            <LoadRow labelId="deployments.groups.details.load.gpu">
              <Text size="sm" c="dimmed">
                <FormattedMessage id="deployments.groups.details.load.gpu.notReported" />
              </Text>
            </LoadRow>
          )}

          <RateRows
            metrics={metrics}
            memberCount={group.members.length}
            worstCameraName={worstCameraName}
          />

          {metrics.loop_lag_ms != null && (
            <LoadRow labelId="deployments.groups.details.load.loopLag">
              <FormattedMessage
                id="deployments.groups.details.load.loopLag.value"
                values={{ ms: formatRate(metrics.loop_lag_ms) }}
              />
            </LoadRow>
          )}
        </Table.Tbody>
      </Table>
    </Paper>
  )
}
